import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DarkUpperSection, LoadingSpinner, PageSection } from '../../../components';
import { useEffect, useMemo, useState } from 'react';
import { useAlert, useCurrentUser, useDatastore } from '../../../hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { UserDetailsProps } from '../../../datastore/userDetails';
import { SUPER_ADMINS, UAT_SUPER_ADMINS } from '../../settings';
import { AdminActionType, logAdminAction } from '../../../services/auditLogger';

export default function AdminPanelAccessSettings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const store = useDatastore();
  const alert = useAlert();
  const user = useCurrentUser();
  const [users, setUsers] = useState<UserDetailsProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingUserIds, setLoadingUserIds] = useState<Set<string>>(new Set());
  const [showReasonDialog, setShowReasonDialog] = useState<{
    email: string;
    id: string;
    adminPanelAccess: boolean;
    reason?: string;
  } | null>(null);

  const adminPanelAccessSettingsAccess = useMemo(() => {
    const superAdminList = process.env.REACT_APP_IS_PROD === 'true' ? SUPER_ADMINS : UAT_SUPER_ADMINS;

    return superAdminList.map((sa) => sa.toLowerCase()).includes(user.email.toLowerCase());
  }, [user.email]);

  useEffect(() => {
    async function fetchUsers() {
      try {
        setIsLoading(true);
        const results = await store.userDetails.getAll();
        if (!results) {
          alert('No users found', 'warning');
          return;
        }
        setUsers(results.sort((a, b) => (a?.email || '').localeCompare(b?.email || '')));
      } catch (error) {
        console.error('Error fetching users:', error);
        alert('Error retrieving users', 'error');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUsers();
  }, [alert, store.userDetails]);

  function handlePageChange(event: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const changeAdminPanelAccess = async ({
    email,
    id,
    adminPanelAccess,
    reason
  }: {
    email: string;
    id: string;
    adminPanelAccess: boolean;
    reason?: string;
  }): Promise<void> => {
    try {
      setLoadingUserIds((prev) => new Set(prev).add(id));
      try {
        await store.userDetails.updateAdminPanelAccess({ id, adminPanelAccess });

        setUsers((prev) => prev.map((user) => (user.id === id ? { ...user, adminPanelAccess } : user)));
      } catch (error) {
        throw error;
      }

      try {
        await logAdminAction({
          actionType: adminPanelAccess
            ? AdminActionType.ADMIN_PANEL_GRANT_ACCESS
            : AdminActionType.ADMIN_PANEL_REVOKE_ACCESS,
          beforeAction: { email, adminAccess: !adminPanelAccess },
          afterAction: { email, adminAccess: adminPanelAccess },
          actionBy: user.email,
          reason
        });
      } catch (auditError) {
        console.error('Failed to log admin action:', auditError);
        alert('Access updated but audit log failed.', 'warning');
      }

      alert(`${adminPanelAccess ? 'Granted access.' : 'Revoked access.'}`, 'success');
    } catch (error) {
      console.error('Error updating user admin panel access:', error);
      alert('Error updating user admin panel access', 'error');
    } finally {
      setLoadingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      setShowReasonDialog(null);
    }
  };

  const normalise = (value: string | null | undefined) => value?.toLowerCase().trim() ?? '';

  const filteredUsers = users
    .filter((user) => user.email)
    .filter((user) => {
      if (!filterText) return true;

      const search = normalise(filterText);

      return (
        normalise(user.firstname).includes(search) ||
        normalise(user.lastname).includes(search) ||
        normalise(user.email).includes(search)
      );
    });
  const PAGE_SIZE = 25;
  const totalNumberOfPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (!adminPanelAccessSettingsAccess) {
    return null;
  }

  return (
    <>
      {showReasonDialog ? (
        <Dialog
          open={!!showReasonDialog}
          onClose={() => setShowReasonDialog(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
          <DialogTitle
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.08)',
              px: 3,
              py: 2.5,
              backgroundColor: '#f9fafb'
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {showReasonDialog?.adminPanelAccess ? 'Grant' : 'Revoke'} Admin Access
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: 3, py: 3, mt: 3 }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1.5,
                display: 'block'
              }}>
              Email
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {showReasonDialog.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                {`Reason for ${showReasonDialog?.adminPanelAccess ? 'Granting' : 'Revoking'} access`}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={showReasonDialog?.reason}
                onChange={(e) =>
                  setShowReasonDialog((prev) => {
                    if (!prev) {
                      return prev;
                    }
                    return { ...prev, reason: e.target.value || '' };
                  })
                }
                placeholder=""
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              backgroundColor: '#fafafa',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
            <Button
              onClick={() => {
                setShowReasonDialog(null);
              }}
              disabled={loadingUserIds.has(showReasonDialog.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                changeAdminPanelAccess(showReasonDialog);
              }}
              disabled={!showReasonDialog?.reason?.trim() || loadingUserIds.has(showReasonDialog.id)}
              sx={{
                px: 2,
                py: 0.75,
                fontWeight: 500,
                fontSize: '0.8rem',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                boxShadow: 'none',
                borderRadius: 1
              }}>
              {loadingUserIds.has(showReasonDialog.id) ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <></>
      )}

      <DarkUpperSection>
        <Stack spacing={4} sx={{ padding: '1em 0' }}>
          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => navigate('/settings')}>
              <ArrowBackIcon fontSize="large" sx={{ color: theme.palette.primary.contrastText }} />
            </IconButton>
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Settings
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <Typography variant="h4" color={theme.palette.primary.contrastText}>
              Modify User Admin Panel Access
            </Typography>
          </Stack>
        </Stack>
      </DarkUpperSection>
      <LoadingSpinner isLoading={isLoading}>
        <PageSection>
          <Stack spacing={4} sx={{ padding: '2em 0' }}>
            <Stack>
              <TextField
                aria-label="Filter"
                label="Filter"
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setCurrentPage(1);
                }}
                variant="outlined"
                sx={{ flexGrow: 1 }}
              />
            </Stack>
          </Stack>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    First Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Last Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Email
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Admin Panel Access
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No users found matching "{filterText}"
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {paginatedUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.firstname}</TableCell>
                  <TableCell>{u.lastname}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        aria-label="Toggle admin panel access"
                        checked={!!u.adminPanelAccess}
                        disabled={loadingUserIds.has(u.id)}
                        onChange={(e) =>
                          setShowReasonDialog({
                            email: u.email || '',
                            id: u.id,
                            adminPanelAccess: e.target.checked
                          })
                        }
                        color="primary"
                      />

                      {loadingUserIds.has(u.id) && <CircularProgress size={16} />}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
            <Pagination count={totalNumberOfPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Stack>
        </PageSection>
      </LoadingSpinner>
    </>
  );
}
